import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    LogOut,
    Menu,
    X,
    ChevronDown,
    ChevronRight,
    Facebook,
    MessageCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils.ts';
import { Button } from '../ui/button';
import { clearAuthData } from '@/services/api';
import { useDashboardAccess } from '@/pages/Meta/Dashboard/api/queries';

interface SidebarChild {
    title: string;
    href: string;
    children?: { title: string; href: string }[];
}

interface SidebarItem {
    title: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
    children?: SidebarChild[];
}

/**
 * Constrói os itens do sidebar. Hoje sem condicionais — todos os items
 * são visíveis pra qualquer user autenticado, exceto Dashboard que é
 * gated pela permissão dashboard_view (default off pra colaboradores).
 */
function buildSidebarItems(hasDashboard: boolean): SidebarItem[] {
    const messageriaChildren: { title: string; href: string }[] = [
        ...(hasDashboard ? [{ title: 'Dashboard', href: '/meta/dashboard' }] : []),
        { title: 'Flow', href: '/meta/flow' },
        { title: 'Broadcasts', href: '/meta/broadcasts' },
        { title: 'Leads', href: '/meta/leads' },
        { title: 'Auto Replies', href: '/meta/comment-auto-replies' },
        { title: 'Quebra-gelo', href: '/meta/ice-breakers' },
        { title: 'Relatórios', href: '/meta/reports/clicks' },
        { title: 'Análise de erros', href: '/meta/error-analysis' },
    ];

    // Esteira + Biblioteca + Social Publisher liberadas pra todos os users autenticados
    messageriaChildren.push({ title: 'Esteira', href: '/meta/esteira' });
    messageriaChildren.push({ title: 'Biblioteca de modelos', href: '/meta/template-library' });
    messageriaChildren.push({ title: 'Template Builder', href: '/meta/template-builder' });
    messageriaChildren.push({ title: 'Social Publisher', href: '/meta/social-publisher' });

    return [
        {
            title: 'Meta',
            href: '/meta',
            icon: Facebook,
            children: [
                {
                    title: 'Messageria',
                    href: '/meta/flow',
                    children: messageriaChildren,
                },
                {
                    title: 'Configurações',
                    href: '/meta/connection',
                    children: [
                        { title: 'Conexões', href: '/meta/connection' },
                        { title: 'AI Keys', href: '/meta/settings/ai-keys' },
                    ],
                },
            ],
        },
    ];
}

export default function Sidebar() {
    const location = useLocation();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    // Default `true` enquanto carrega/falha — evita flash do item sumindo.
    // Backend é a fonte da verdade (gate por endpoint); UI só esconde o link.
    const { data: dashAccess } = useDashboardAccess();
    const hasDashboard = dashAccess?.enabled !== false;
    const visibleItems = buildSidebarItems(hasDashboard);
    const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

    // Auto-expand menus if on their route
    useEffect(() => {
        if (location.pathname.startsWith('/meta')) {
            setExpandedMenus((prev) => {
                let next = prev.includes('/meta') ? prev : [...prev, '/meta'];
                const messageriaRoutes = ['/meta/flow', '/meta/broadcasts', '/meta/leads', '/meta/comment-auto-replies', '/meta/social-publisher', '/meta/error-analysis', '/meta/esteira', '/meta/template-library', '/meta/template-builder'];
                if (messageriaRoutes.some((r) => location.pathname.startsWith(r))) {
                    next = next.includes('meta-messageria') ? next : [...next, 'meta-messageria'];
                }
                if (location.pathname.startsWith('/meta/settings') || location.pathname.startsWith('/meta/connection')) {
                    next = next.includes('meta-configurações') ? next : [...next, 'meta-configurações'];
                }
                return next;
            });
        }
    }, [location.pathname]);

    const toggleMenu = (key: string) => {
        setExpandedMenus((prev) =>
            prev.includes(key) ? prev.filter((h) => h !== key) : [...prev, key]
        );
    };

    const handleLogout = () => {
        clearAuthData();
        navigate('/login');
    };

    useEffect(() => { setIsOpen(false); }, [location.pathname]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const sidebar = document.getElementById('sidebar');
            const menuButton = document.getElementById('menu-button');
            if (
                isOpen
                && sidebar && !sidebar.contains(event.target as Node)
                && menuButton && !menuButton.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : 'unset';
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    return (
        <>
            <button
                id="menu-button"
                onClick={() => setIsOpen(!isOpen)}
                className="fixed top-4 left-4 z-50 lg:hidden bg-gray-900 text-white p-2 rounded-lg shadow-lg hover:bg-gray-800 transition-colors"
                aria-label="Toggle menu"
            >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            {isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden" onClick={() => setIsOpen(false)} />
            )}

            <div
                id="sidebar"
                className={cn(
                    'fixed lg:static inset-y-0 left-0 z-40 flex h-screen w-64 flex-col bg-gray-900 text-white transition-transform duration-300 ease-in-out',
                    isOpen ? 'translate-x-0' : '-translate-x-full',
                    'lg:translate-x-0'
                )}
            >
                <div className="flex h-16 items-center justify-center border-b border-gray-800">
                    <h1 className="text-xl font-bold">conectablog</h1>
                </div>

                <nav className="flex-1 space-y-1 overflow-y-auto p-4">
                    {visibleItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = item.children
                            ? item.children.some((child: SidebarChild) => {
                                if (child.children) {
                                    return child.children.some((sc) => location.pathname.startsWith(sc.href));
                                }
                                return location.pathname.startsWith(child.href);
                            })
                            : location.pathname === item.href;
                        const isExpanded = expandedMenus.includes(item.href);

                        if (item.children) {
                            return (
                                <div key={item.href}>
                                    <button
                                        onClick={() => toggleMenu(item.href)}
                                        className={cn(
                                            'flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                            isActive ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                        )}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Icon className="h-5 w-5" />
                                            {item.title}
                                        </div>
                                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </button>
                                    {isExpanded && (
                                        <div className="ml-4 mt-1 space-y-1 border-l border-gray-700 pl-4">
                                            {item.children.map((child: SidebarChild) => {
                                                if (child.children) {
                                                    const subKey = `${item.href.replace('/', '')}-${child.title.toLowerCase()}`;
                                                    const isSubExpanded = expandedMenus.includes(subKey);
                                                    const isSubActive = child.children.some((sc) => location.pathname.startsWith(sc.href));

                                                    return (
                                                        <div key={subKey}>
                                                            <button
                                                                onClick={() => toggleMenu(subKey)}
                                                                className={cn(
                                                                    'flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-sm transition-colors',
                                                                    isSubActive ? 'text-white font-medium' : 'text-gray-500 hover:text-white'
                                                                )}
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <MessageCircle className="h-3.5 w-3.5" />
                                                                    {child.title}
                                                                </div>
                                                                {isSubExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                                            </button>
                                                            {isSubExpanded && (
                                                                <div className="ml-3 mt-0.5 space-y-0.5 border-l border-gray-700/50 pl-3">
                                                                    {child.children.map((subChild) => (
                                                                        <Link
                                                                            key={subChild.href}
                                                                            to={subChild.href}
                                                                            className={cn(
                                                                                'block rounded-lg px-3 py-1.5 text-sm transition-colors',
                                                                                location.pathname.startsWith(subChild.href)
                                                                                    ? 'text-white font-medium'
                                                                                    : 'text-gray-500 hover:text-white'
                                                                            )}
                                                                        >
                                                                            {subChild.title}
                                                                        </Link>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <Link
                                                        key={child.href}
                                                        to={child.href}
                                                        className={cn(
                                                            'block rounded-lg px-3 py-1.5 text-sm transition-colors',
                                                            location.pathname === child.href
                                                                ? 'text-white font-medium'
                                                                : 'text-gray-500 hover:text-white'
                                                        )}
                                                    >
                                                        {child.title}
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        const isItemActive = location.pathname === item.href;
                        return (
                            <Link
                                key={item.href}
                                to={item.href}
                                className={cn(
                                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                    isItemActive ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                )}
                            >
                                <Icon className="h-5 w-5" />
                                {item.title}
                            </Link>
                        );
                    })}
                </nav>

                <div className="border-t border-gray-800 p-4">
                    <Button
                        variant="ghost"
                        className="w-full justify-start text-gray-400 hover:bg-gray-800 hover:text-white"
                        onClick={handleLogout}
                    >
                        <LogOut className="mr-3 h-5 w-5" />
                        Sair
                    </Button>
                </div>
            </div>
        </>
    );
}
